/**
 * Phase 9.3 (2026-05-24) — map a `tool_progress` WS frame's (tool, status)
 * pair to a user-facing string for the VoicePanel tool-activity strip.
 *
 * Schema-extensible per CONC-9.3-1: backend may add new tools without a
 * frontend ship; the renderer falls back to `${tool}: ${status}` for
 * unknown tools. Within the 11 known _TOOLS (post-Phase-9.1), the matrix
 * is exhaustively populated — CONC-9.3-6 lock.
 *
 * R1.1 wording lock: `web_search.completed` → "web search complete" and
 * `corpus_query.completed` → "corpus query complete" (not "done") to
 * match the past-tense register of the other 9 tools.
 */
export type ToolProgressStatus = 'starting' | 'completed' | 'failed';

const STRINGS: Record<string, Record<ToolProgressStatus, string>> = {
  spawn_terminal: {
    starting: 'spawning terminals…',
    completed: 'spawned terminals',
    failed: 'spawn failed',
  },
  send_to_pane: {
    starting: 'sending to pane…',
    completed: 'sent to pane',
    failed: 'send failed',
  },
  list_panes: {
    starting: 'listing panes…',
    completed: 'listed panes',
    failed: 'list failed',
  },
  navigate: {
    starting: 'navigating…',
    completed: 'navigated',
    failed: 'navigate failed',
  },
  web_search: {
    starting: 'searching the web…',
    completed: 'web search complete',
    failed: 'search failed',
  },
  send_to_pi_chat: {
    starting: 'sending to Pi chat…',
    completed: 'sent to Pi chat',
    failed: 'Pi chat send failed',
  },
  corpus_query: {
    starting: 'searching the corpus…',
    completed: 'corpus query complete',
    failed: 'corpus query failed',
  },
  spa_action: {
    starting: 'updating the view…',
    completed: 'view updated',
    failed: 'view update failed',
  },
  ingest_text: {
    starting: 'remembering text…',
    completed: 'text remembered',
    failed: 'text ingest failed',
  },
  ingest_url: {
    starting: 'ingesting URL…',
    completed: 'URL ingested',
    failed: 'URL ingest failed',
  },
  ingest_repo: {
    starting: 'ingesting repo…',
    completed: 'repo ingested',
    failed: 'repo ingest failed',
  },
};

export function humanizeTool(tool: string, status: ToolProgressStatus): string {
  const byTool = STRINGS[tool];
  if (!byTool) {
    // Unknown tool fallback per CONC-9.3-1 extensibility.
    return `${tool}: ${status}`;
  }
  return byTool[status];
}

/** Exported for tests asserting the 11×3 = 33 known matrix entries. */
export const KNOWN_TOOLS = Object.keys(STRINGS);
